/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import {
  DataTable,
  TextV2,
  Counter,
  Chip,
} from '@insights-ltd/design-library/components';
import { Role } from 'types/types';
import { GroupResponse, PractitionerSummaryData } from 'api';
import useSearchFilter from 'components/hooks/useSearchFilter';
import LeadingColourEnergyIndicator from 'components/LeadingColourEnergyIndicator';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { styled, useTheme } from '@mui/material';
import { formatDateFromIsoString } from '@insights-ltd/design-library/utils';
import LinkEmail from 'components/LinkEmail/LinkEmail';
import { SearchFilter } from 'components/SearchFilter';
import { roleDescription } from './helper';
import { InviteUserAction } from './InviteUserAction';

type OrganisationUsersProps = {
  practitioners: PractitionerSummaryData[];
  title: string;
  organisationId: string;
  organisationGroup: GroupResponse | null | undefined;
};

const PRACTITIONER_SEARCH_FIELD_NAME = 'fullName';
const PRACTITIONER_SEARCH_FIELD_EMAIL_ADDRESS = 'emailAddress';

const ListItemWrapperDiv = styled('div')(({ theme }) => ({
  '& > :first-of-type': {
    marginBottom: `${theme.spacing(spacingSizeMap.M)}`,
  },
  '& > * + *': {
    marginBottom: `${theme.spacing(spacingSizeMap.XS)} !important`,
  },
}));

const sortByDate = (items: PractitionerSummaryData[]) =>
  items.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

const OrganisationPractitioners = ({
  practitioners,
  title,
  organisationId,
  organisationGroup,
}: OrganisationUsersProps) => {
  const { filteredItems, handleTermChange, searchTerm } = useSearchFilter(
    practitioners,
    [PRACTITIONER_SEARCH_FIELD_NAME, PRACTITIONER_SEARCH_FIELD_EMAIL_ADDRESS],
  );
  const sortedPractitioners = sortByDate(filteredItems!);

  const { t, i18n } = useTranslation();
  const localTheme = useTheme();
  const { language: locale } = i18n;

  const dataStructure = {
    fullName: t('ui.event-management.organisation.practitioners.list.name'),
    emailAddress: t(
      'ui.event-management.organisation.practitioners.list.email',
    ),
    role: t('ui.event-management.organisation.practitioners.list.role'),
    createdAt: t(
      'ui.event-management.organisation.practitioners.list.date-created',
    ),
    totalEvents: t(
      'ui.event-management.organisation.practitioners.list.total-experiences',
    ),
  };

  const columnPredicate = (
    columnName: keyof PractitionerSummaryData,
    columnValue: string | number | Role,
    props: Record<any, any>,
  ) => {
    switch (columnName) {
      case 'fullName':
        return (
          <Button
            color="primary"
            sx={() => ({
              justifyContent: 'flex-start',
              maxWidth: '15rem',
              paddingLeft: 0,
              textAlign: 'left',
              wordBreak: 'break-word',
              '& > :first-of-type': {
                marginLeft: `0 !important`,
              },
              padding: `0 !important`,
            })}
            component={RouterLink}
            to={`/organisations/${organisationId}/practitioners/${props.id}`}
          >
            <LeadingColourEnergyIndicator
              leadingColourEnergy={props.leadingColourEnergy}
            />
            {columnValue}
            {!props.enabled && (
              <Chip
                label={t('ui.event-management.access.no-access')}
                color="grey"
              />
            )}
          </Button>
        );
      case 'emailAddress':
        return <LinkEmail email={columnValue as string} />;
      case 'role': {
        return t(roleDescription(columnValue as Role));
      }

      case 'createdAt':
        return <>{formatDateFromIsoString(columnValue as string, locale)}</>;
      default:
        return columnValue;
    }
  };

  const labelText = `${t('ui.event-management.list-filter.search')} ${t(
    'ui.event-management.list-filter.by-name-email',
  )}`;
  return (
    <ListItemWrapperDiv>
      <SearchFilter
        labelText={labelText}
        placeholder={t(
          'ui.event-management.organisation.practitioners.list.search.placeholder',
        )}
        handleTermChange={handleTermChange}
        inputState={searchTerm}
        showButton={false}
        allowEmptySearches={false}
        minCharacters={1}
      />
      <Grid
        container
        sx={{
          alignItems: 'baseline',
          justifyContent: 'space-between',
        }}
      >
        <Grid
          sx={{ marginBottom: '10px !important' }}
          size={{ xs: 12, sm: 'auto' }}
        >
          <TextV2 variant="h3">
            {title}
            <Counter
              count={filteredItems!.length}
              size="large"
              sx={{
                marginLeft: 1,
              }}
              dataTestId="count-box"
            />
          </TextV2>
        </Grid>

        <Grid size={{ xs: 12, sm: 'auto' }}>
          <InviteUserAction
            organisationId={organisationId}
            organisationGroup={organisationGroup}
          />
        </Grid>
      </Grid>
      {sortedPractitioners.length ? (
        <DataTable
          items={sortedPractitioners}
          dataStructure={dataStructure}
          itemsPerPage={12}
          columnPredicate={columnPredicate}
          translations={{
            nextLabel: t('ui.common.pagination.next'),
            prevLabel: t('ui.common.pagination.prev'),
          }}
        />
      ) : (
        <TextV2
          variant="h3"
          color="textSecondary"
          sx={{
            marginTop: `${localTheme.spacing(spacingSizeMap.L)} !important`,
            textAlign: 'center',
          }}
        >
          {t(
            practitioners.length < 1
              ? 'ui.event-management.organisation.practitioners.list.empty'
              : 'ui.event-management.organisation.practitioners.list.filtered',
          )}
        </TextV2>
      )}
    </ListItemWrapperDiv>
  );
};

export default OrganisationPractitioners;
/* eslint-enable @typescript-eslint/no-non-null-assertion */
